import axiosInstance from "./axiosInstance";
import { AxiosResponse } from 'axios';

export interface VerifyOTPRequest {
  code: string;
}

export interface TOTPResponse {
  secret?: string;
  qr_code?: string;
  status: string;
  message?: string;
}

export interface TOTPStatusResponse {
  status: boolean;
}

export class OTPService {
  private static readonly WITHDRAW_OTP_ENDPOINT = '/app/otps/withdraw';
  private static readonly TOTP_ENDPOINT = '/app/otps/totp';

  /**
   * Request a new OTP for withdrawal verification
   * @returns Promise with the response data
   * @throws Error with appropriate message if request fails
   */
  public static async requestWithdrawOTP(): Promise<any> {
    try {
      const res: AxiosResponse<any> = await axiosInstance.get(OTPService.WITHDRAW_OTP_ENDPOINT);
      return res.data;
    } catch (error: any) {
      // Properly handle 400 error which occurs when phone number is missing
      if (error?.response?.status === 400) {
        const errorData = error.response.data;
        throw {
          ...error,
          response: {
            ...error.response,
            data: errorData
          }
        };
      }
      throw error;
    }
  }

  /**
   * Verify an OTP for withdrawal
   * @param data The OTP verification request containing the code
   * @returns Promise with the response data
   */
  public static async verifyWithdrawOTP(data: VerifyOTPRequest): Promise<any> {
    const res: AxiosResponse<any> = await axiosInstance.post(OTPService.WITHDRAW_OTP_ENDPOINT, data);
    return res.data;
  }

  /**
   * Generate a new TOTP secret and QR code
   * @returns Promise with the TOTP data including secret and QR code
   * @throws Error with appropriate message if request fails or if TOTP is already set up (status: "DUPLICATE")
   */
  public static async generateTOTP(): Promise<TOTPResponse> {
    const res: AxiosResponse<TOTPResponse> = await axiosInstance.get(OTPService.TOTP_ENDPOINT);
    return res.data;
  }

  /**
   * Verify a TOTP code
   * @param data The TOTP verification request containing the code
   * @returns Promise with the verification result
   */
  public static async verifyTOTP(data: VerifyOTPRequest): Promise<TOTPResponse> {
    const res: AxiosResponse<TOTPResponse> = await axiosInstance.post(OTPService.TOTP_ENDPOINT, data);
    return res.data;
  }

  /**
   * Check if TOTP is set up for the current user
   * @returns Promise with the status (true if TOTP is set up, false otherwise)
   */
  public static async getTOTPStatus(): Promise<TOTPStatusResponse> {
    const res: AxiosResponse<TOTPStatusResponse> = await axiosInstance.get(`${OTPService.TOTP_ENDPOINT}/status`);
    return res.data;
  }
}
